import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#006cbe', '#6440fb', '#04aa6d', '#f59e0b', '#ef4444', '#8b5cf6'];

function StatCard({ label, value, color = '', sub }) {
  return (
    <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${color}`}>{(value ?? 0).toLocaleString()}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/reports/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 60 }}>
      <div className="spinner" />
    </div>
  );

  const trend = stats?.monthly_trend || [];
  const topAreas = stats?.top_solution_areas || [];

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Partner learning activity overview</p>
      </div>

      <div className="stats-grid">
        <StatCard label="Total Learners" value={stats?.total_users} color="blue" sub="Verified active users" />
        <StatCard label="Total Completions" value={stats?.total_completions} color="" sub="FY26 · includes archived courses" />
        <StatCard label="AI Completions" value={stats?.ai_completions} color="green" sub="AI-tagged courses" />
        <StatCard label="Active Enrollments" value={stats?.active_enrollments} color="purple" sub="In progress" />
        <StatCard label="This Month" value={stats?.completions_this_month} color="" sub="Completions" />
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-title">Monthly Completions (Last 12 Months)</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => [v.toLocaleString(), 'Completions']} />
              <Bar dataKey="count" fill="#006cbe" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <div className="chart-title">Top Solution Areas</div>
          {topAreas.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={topAreas} dataKey="count" nameKey="solution_area"
                  cx="50%" cy="50%" outerRadius={75} label={({ solution_area, percent }) =>
                    `${(solution_area || '').substring(0, 14)} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {topAreas.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => [v.toLocaleString(), 'Completions']} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="empty-state"><p>No data available</p></div>}
        </div>
      </div>
    </div>
  );
}
